import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Dropdown } from '../Dropdown';
import {
  Circles,
  ColorDots,
  ColorsDiv,
  ContentDiv,
  DropDiv,
  EmptyText,
  Icon,
  IconsContainer,
  IconsDiv,
  ImageContainer,
  Input,
  SearchDiv,
  SVGIconDiv,
  TabButtonDiv,
  TabItem,
  UploadButton,
  UploadImageDiv,
  UploadTextDiv
} from './style';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import defaultImageLightMode from '../../assets/images/IcondefaultImage.svg';
import defaultImageDarkMode from '../../assets/images/IcondefaultImageDark.svg';
// import data from '@emoji-mart/data';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ICON_COLORS, PROJECT_ICON_TYPE } from '../../global/constants';
import IconSvg from '../../assets/images/svg/iconSvg';
import { projectIcon } from '../../global/row';
import ProjectIconAvatar from '../ProjectIconAvtar/projectIconAvtar';
import { Icon as IconInterface } from '../../mobx/interfaces/project';
import { useDispatch, useSelector } from 'react-redux';
import { dispatchProjectSuccess, updateProjectData } from '../../services/projectServices';
import { ProjectModel } from '../../mobx/models/project';
import { captureException } from '../../services/logService';
import { getFirebaseUrlFromFile } from '../../helpers/common';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { useMobileDevice } from '../../global/useMobile';
import { selectAppState } from '../../store/selectors';

interface Props {
  currentProject?: ProjectModel;
  iconSize?: number;
  size?: number;
  isCreateProject?: boolean;
}
const AddIconDropdown = (props: Props) => {
  const isMobile = useMobileDevice();
  const { currentProject, iconSize = 16, size = isMobile ? 30 : 26, isCreateProject } = props;
  //Ref
  const inputRef = useRef<HTMLInputElement | null>(null);
  const iconDropdownRef = useRef<{ closeDropdown: () => void }>(null);

  //State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'icons' | 'emojis' | 'image'>('icons');
  //Mobx store
  const { viewStore, projectStore } = rootStore;
  const { createViewInput } = viewStore;
  const { createProjectInput } = projectStore;
  //Other variables
  const dispatch = useDispatch();
  const project = isCreateProject ? createProjectInput : rootStore.projectStore.currentProject;

  // Get theme mode from Redux
  const { themeMode } = useSelector(selectAppState);

  // Get all custom icons
  const allIcons = useMemo(() => {
    return projectIcon.map((icon) => ({
      name: icon.label,
      displayName: icon.label
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      component: (props: { size?: number }) => (
        <IconSvg name={icon.label} width='16px' height='16px' viewBox='0 0 20 20' fill='var(--text-secondary)' />
      )
    }));
  }, []);

  // Filter icons based on search query
  const filteredIcons = useMemo(() => {
    if (!searchQuery) return allIcons;
    return allIcons.filter((icon) => icon.displayName.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [allIcons, searchQuery]);

  const handleSelectProjectIcon = useCallback(
    async (type: number, reference: string) => {
      try {
        iconDropdownRef.current?.closeDropdown();
        if (reference === projectIcon[0].label) {
          type = PROJECT_ICON_TYPE.DEFAULT;
          reference = '';
        }
        let updatedIcon: IconInterface;
        if (isCreateProject) {
          updatedIcon = {
            ...createProjectInput?.icon,
            type,
            reference
          };
          createProjectInput?.setIcon(updatedIcon);
        } else {
          updatedIcon = {
            ...currentProject?.icon,
            type,
            reference
          };
          const payload = {
            Icon: {
              Type: type,
              Reference: reference,
              Color: currentProject?.icon?.color
            }
          };
          rootStore.projectStore.currentProject?.setIcon(updatedIcon);
          const response = await dispatch(updateProjectData(currentProject?.id, payload));
          if (response) {
            dispatchProjectSuccess('Project icon updated successfully');
            return true;
          }
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [createProjectInput, currentProject?.icon, currentProject?.id, dispatch, isCreateProject]
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const onSelectColor = useCallback(
    async (color: number) => {
      try {
        let updatedProjectIcon: IconInterface;
        if (isCreateProject) {
          updatedProjectIcon = {
            ...createProjectInput?.icon,
            color
          };
          createProjectInput?.setIcon(updatedProjectIcon);
        } else {
          updatedProjectIcon = {
            ...currentProject?.icon,
            color
          };
          rootStore.projectStore.currentProject?.setIcon(updatedProjectIcon);
          const payload = {
            Icon: {
              Type: currentProject?.icon?.type || PROJECT_ICON_TYPE.DEFAULT,
              Reference: currentProject?.icon?.reference,
              Color: color
            }
          };
          const response = await dispatch(updateProjectData(currentProject?.id, payload));
          if (response) {
            dispatchProjectSuccess('Project icon updated successfully');
            return true;
          }
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [createProjectInput, currentProject?.icon, currentProject?.id, dispatch, isCreateProject]
  );

  // handel upload click
  const handleUploadClick = useCallback(() => {
    inputRef.current?.click();
  }, []);
  // for convert base64
  const convertBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = async () => {
        const item = {
          profile: fileReader.result,
          file: file,
          name: currentProject?.name
        };
        let profileUrl;
        if (item) {
          profileUrl = await getFirebaseUrlFromFile(item, `project-icon/`, dispatch, currentProject?.id);
        }
        const updatedIcon = {
          type: PROJECT_ICON_TYPE.IMAGE,
          reference: profileUrl
        };
        if (isCreateProject) {
          rootStore.projectStore.createProjectInput?.setIcon(updatedIcon);
        } else {
          rootStore.projectStore.currentProject?.setIcon(updatedIcon);
          const payload = {
            Icon: {
              Type: PROJECT_ICON_TYPE.IMAGE,
              Reference: profileUrl
            }
          };
          const response = await dispatch(updateProjectData(currentProject?.id, payload));
          if (response) rootStore.alertNotificationStore.setSuccessMessage('Customer profile updated successfully!');
        }
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  // handle file read
  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      let fileData = file?.name.split('.');
      if (file.size > 500000) {
        rootStore.alertNotificationStore.setErrorMessage('Please upload less than 500kb photo size.');
        return false;
      }
      iconDropdownRef.current?.closeDropdown();
      const fileType = fileData[fileData.length - 1];
      if (fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png') {
        await convertBase64(file);
      } else {
        rootStore.alertNotificationStore.setErrorMessage('Please upload a valid type photo.');
        return false;
      }
    } catch (e) {
      captureException(e);
      console.log('error', e);
    }
  };

  return (
    <>
      <input type='file' onChange={handleFileRead} ref={inputRef} style={{ display: 'none' }} />

      <Dropdown
        isMinWidth={303}
        isRemoveMaxHeight={true}
        isRemovePadding={true}
        ref={iconDropdownRef}
        content={
          <>
            {isCreateProject && !createProjectInput?.icon && !createProjectInput?.name ? (
              <SVGIconDiv>
                <SVGIcon name='pie-chart-icon' width='20' height='20' viewBox='0 0 14 14' fill='var(--text-muted)' />
              </SVGIconDiv>
            ) : (
              <ProjectIconAvatar
                size={size}
                type={project?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
                reference={project?.icon?.reference || ''}
                backgroundColor={project?.icon?.color ? ICON_COLORS[project?.icon?.color] : ''}
                projectName={project?.name || ''}
                iconSize={iconSize}
                isClickable={true}
              />
            )}
          </>
        }>
        <DropDiv onClick={(e) => e.stopPropagation()}>
          <TabButtonDiv>
            <TabItem
              isActive={activeTab === 'icons'}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('icons');
              }}>
              Icons
            </TabItem>
            <TabItem
              isActive={activeTab === 'emojis'}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('emojis');
              }}>
              Emojis
            </TabItem>
            <TabItem
              isActive={activeTab === 'image'}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab('image');
              }}>
              Image
            </TabItem>
          </TabButtonDiv>
          <ContentDiv>
            {activeTab === 'icons' && (
              <ColorsDiv>
                <Circles>
                  <ColorDots>
                    {Object.entries(ICON_COLORS).map(([key, color]) => (
                      <span key={key} style={{ background: color }} onClick={() => onSelectColor(Number(key))}>
                        {/* show icon of selected color */}
                        {project?.icon?.color === Number(key) && (
                          <SVGIcon
                            name='correct-sign-icon'
                            width='14'
                            height='14'
                            viewBox='0 0 18 18'
                            fill='var(--text-white)'
                          />
                        )}
                      </span>
                    ))}
                  </ColorDots>
                </Circles>
                {/* <LineDiv />
            <IconButton>
              <SVGIcon name='color-palette-icon' width='14' height='14' viewBox='0 0 14 14' />
            </IconButton> */}
              </ColorsDiv>
            )}
            {activeTab === 'icons' ? (
              <IconsDiv>
                <SearchDiv onClick={(e) => e.stopPropagation()}>
                  <Input placeholder='Search icons' value={searchQuery} onChange={handleSearch} />
                </SearchDiv>
                <IconsContainer>
                  {filteredIcons.map((icon) => {
                    const IconComponent = icon.component;
                    return (
                      <Icon
                        key={icon.name}
                        onClick={() => handleSelectProjectIcon(PROJECT_ICON_TYPE.ICON, icon.name)}
                        style={{
                          background:
                            createViewInput.icon?.image === icon.name ? 'var(--background-secondary)' : 'transparent'
                        }}>
                        <IconComponent size={16} />
                      </Icon>
                    );
                  })}
                </IconsContainer>
                {searchQuery && filteredIcons.length === 0 && <EmptyText>No results found</EmptyText>}
              </IconsDiv>
            ) : activeTab === 'emojis' ? (
              <IconsDiv>
                <EmojiPicker
                  searchPlaceholder='Search Emoji'
                  theme={Theme.AUTO}
                  autoFocusSearch={true}
                  width={258}
                  height={240}
                  onEmojiClick={(data) => {
                    handleSelectProjectIcon(PROJECT_ICON_TYPE.EMOJI, data?.emoji);
                  }}
                  lazyLoadEmojis={true}
                />
              </IconsDiv>
            ) : (
              <IconsDiv>
                {/* Placeholder for image upload/select UI */}
                <UploadImageDiv>
                  <ImageContainer>
                    <img
                      // eslint-disable-next-line max-len
                      src={
                        project?.icon?.type === PROJECT_ICON_TYPE.IMAGE && project?.icon?.reference
                          ? project.icon.reference
                          : themeMode?.theme === 'dark'
                          ? defaultImageDarkMode
                          : defaultImageLightMode
                      }
                      alt='profilePhoto'
                      width={48}
                      height={48}
                    />
                  </ImageContainer>
                  <UploadTextDiv>
                    <UploadButton onClick={handleUploadClick}> Upload image</UploadButton>
                    <p>Maximum file size allowed in 500 KB in jpg, jpeg and png format.</p>
                  </UploadTextDiv>
                </UploadImageDiv>
              </IconsDiv>
            )}
          </ContentDiv>
        </DropDiv>
      </Dropdown>
    </>
  );
};

export default observer(AddIconDropdown);
